import { isAgentHubEnabled } from 'agentHub/selectors/isAgentHubEnabled'
import useIsHomepage from 'home/hooks/isHomepage'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { haveOrdersBeenFetched } from 'lib/order/orderUtils'
import { useEffect, useRef } from 'react'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { canStartAgentHubBookingOnboarding } from './canStartAgentHubBookingOnboarding'
import type { AgentHubBookingOnboardingStatus } from './agentHubBookingOnboardingStorage'
import { fetchUpcomingOrdersForAgentHubBookingOnboarding } from './fetchUpcomingOrdersForAgentHubBookingOnboarding'

const START_DELAY_MS = 800
const UPCOMING_ORDER_LIST: App.OrderListTypes = 'upcoming'

function useAgentHubBookingOnboardingAutoStart(options: {
  isActive: boolean
  start: () => void
  status?: AgentHubBookingOnboardingStatus
}) {
  const { isActive, start, status } = options
  const dispatch = useAppDispatch()
  const isHomepage = useIsHomepage()
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const hasOrders = useAppSelector(state => Object.keys(state.orders.orders).length > 0)
  const ordersHaveBeenFetched = useAppSelector(state => haveOrdersBeenFetched(state, UPCOMING_ORDER_LIST))
  const ordersFetchAttemptedForMemberRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (!isAgentHubEnabled || !isHomepage || !isLoggedIn || !memberId) {
      ordersFetchAttemptedForMemberRef.current = undefined
      return
    }

    if (ordersFetchAttemptedForMemberRef.current === memberId) {
      return
    }

    ordersFetchAttemptedForMemberRef.current = memberId
    dispatch(fetchUpcomingOrdersForAgentHubBookingOnboarding())
  }, [dispatch, isHomepage, isLoggedIn, memberId])

  useEffect(() => {
    if (isActive) {
      return
    }

    if (!canStartAgentHubBookingOnboarding({
      hasOrders,
      haveOrdersBeenFetched: ordersHaveBeenFetched,
      isAgentHubEnabled,
      isHomepage,
      isLoggedIn,
      isSpoofed,
      memberId,
      storedStatus: status,
    })) {
      return
    }

    const startTimer = window.setTimeout(() => {
      start()
    }, START_DELAY_MS)

    return () => {
      window.clearTimeout(startTimer)
    }
  }, [hasOrders, isActive, isHomepage, isLoggedIn, isSpoofed, memberId, ordersHaveBeenFetched, start, status])
}

export default useAgentHubBookingOnboardingAutoStart
